import React, { useEffect, useState, useRef, useCallback } from 'react';
import { isMobile } from 'react-device-detect';
import '../../fonts/fonts.css';
import { COLORS, getValueWithoutUnit } from '../../utils';

const CANVAS_SIZE = 565;

const getColor = (color) => {
    const result = COLORS.filter((item) => {
        return item.name === color;
    });
    return result[0]?.hex;
};

const Image = ({ design, name, lastName, color, font }) => {
    const coordStyles = useRef([]);
    const [designLoaded, setDesignLoaded] = useState(false);
    const containerRef = useRef(null);
    const [scale, setScale] = useState(1);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const observer = new ResizeObserver(([entry]) => {
            const width = entry.contentRect.width;
            if (width > 0) setScale(width / CANVAS_SIZE);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const textAlignment = () => {
        switch (design?.defaultAlignment) {
            case 'left':
                return 'text-left';
            case 'center':
                return 'text-center';
            case 'right':
                return 'text-right';
            default:
                return 'text-center';
        }
    };

    const labelStyles = `absolute z-10 p-1 py-2 md:py-2.5 drop-shadow-md ${textAlignment()} ${
        font !== 'ThankYou' ? 'leading-5' : ''
    } ${font ? `font_${font}` : `font_${design?.defaultTypography}`} ${
        color ? `palette_${getColor(color)}` : `palette_${design?.defaultColor}`
    }`;

    const nStickers = design?.labels.length;

    const addCoords = useCallback(() => {
        design?.labels.forEach((label, i) => {
            const specialTypo = font === 'ThankYou';
            coordStyles.current[label.position] = {
                top: `${label.top || 0}`,
                left: `${label.left || 0}`,
                width: `${label.width || 0}`,
                fontSize: `${label.fontSize || '14px'}`,
            };

            if (specialTypo) {
                const fontSizeWithoutUnit = getValueWithoutUnit(label?.fontSize, 'px');
                coordStyles.current[label.position].lineHeight = `${Number(fontSizeWithoutUnit) + 6}px`;
            }
        });
        setDesignLoaded(!designLoaded);
    }, [design, designLoaded, font]);

    useEffect(() => {
        addCoords();
        // eslint-disable-next-line
    }, [design]);

    const scaleCoords = (raw) => {
        if (!raw || scale === 1) return raw;
        const scaled = { ...raw };
        ['top', 'left', 'width', 'fontSize', 'lineHeight'].forEach((prop) => {
            if (scaled[prop]) {
                const num = parseFloat(scaled[prop]);
                if (!isNaN(num)) {
                    const unit = scaled[prop].replace(/[\d.]/g, '') || 'px';
                    scaled[prop] = `${num * scale}${unit}`;
                }
            }
        });
        return scaled;
    };

    return (
        <div
            ref={containerRef}
            className={`bg-white sticky self-start md:bg-transparent z-20 shadow-lg -top-10 md:top-0 md:mt-10 md:mx-auto`}
            style={{ width: '100%', maxWidth: `${CANVAS_SIZE}px` }}>
            <div className="relative w-full" style={{ paddingBottom: `${(CANVAS_SIZE / CANVAS_SIZE) * 100}%` }}>
                {nStickers &&
                    design?.labels.map((label, i) => {
                        const raw = coordStyles.current[i];
                        const scaled = scaleCoords(raw);
                        const extraStyling = {};
                        if (scaled) {
                            const fontSize = parseFloat(scaled.fontSize);
                            extraStyling.lh = fontSize < 16 ? 'et-lh-13' : '';
                        }
                        return (
                            <div
                                key={`label-${label.position}-${i}`}
                                className={`${labelStyles} ${extraStyling.lh}`}
                                style={scaled}>
                                {name} {lastName}
                            </div>
                        );
                    })}
                {design?.image ? (
                    <img
                        onContextMenu={() => false}
                        className="absolute inset-0 w-full h-full z-0"
                        src={design?.image}
                        alt="Previsualiza"
                    />
                ) : (
                    <>
                        {isMobile && (
                            <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-center">
                                Selecciona un diseño
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Image;
